import { type QueryKey, useIsMutating, useQueryClient } from "@tanstack/react-query";
import type {
	ApiResponse,
	ProviderOperationsActionPostRequest,
	ProviderOperationsSearchResponse,
} from "libs/apiClient";
import client from "libs/client";
import { useToast } from "ui-kit";
import { getProviderOperationsErrorMessage } from "./getProviderOperationsErrorMessage";
import { isGameScopedAction } from "./isGameScopedAction";
import { createGameRowActionMutationKey } from "./providerOperationsMutationKeys";
import { replaceProviderOperationsRows } from "./replaceProviderOperationsRows";

export function useProviderOperationsAction(searchQueryKey: QueryKey, gameId: number) {
	const queryClient = useQueryClient();
	const { addErrorToast } = useToast();

	return client.postProviderOperationsAction.useMutation({
		mutationKey: createGameRowActionMutationKey(gameId),
		onSuccess: (response, variables) => {
			if (response === undefined) {
				return;
			}

			queryClient.setQueryData<ApiResponse<ProviderOperationsSearchResponse>>(searchQueryKey, (searchResult) =>
				replaceProviderOperationsRows(searchResult, [response.data]),
			);

			if (isGameScopedAction(variables.body.action)) {
				// Keeps the mutation pending until sibling rows have refetched their actions.
				return queryClient.invalidateQueries(searchQueryKey);
			}
		},
		onError: (error) => {
			addErrorToast(getProviderOperationsErrorMessage(error.json));
		},
	});
}

/** True while any row of the game is running a game-scoped action. */
export function useIsGameScopedActionRunning(gameId: number): boolean {
	const runningCount = useIsMutating({
		mutationKey: createGameRowActionMutationKey(gameId),
		predicate: (mutation) => {
			const variables = mutation.state.variables as { body: ProviderOperationsActionPostRequest } | undefined;

			return variables !== undefined && isGameScopedAction(variables.body.action);
		},
	});

	return runningCount > 0;
}
